import { useState, useEffect, useRef, useCallback } from 'react';
import db from '@/db';
import { classifyInput } from '@/lib/barcode';
import {
  getOrReuseCommand, appendWeightToCommand, addItemToCommand, closeCommand,
} from '@/lib/commands';
import { findProductByBarcode, linkBarcodeToBeverage } from '@/lib/productBarcode';
import { formatCurrency } from '@/lib/formatters';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Scan, Barcode, CreditCard, X, CheckCircle2, Banknote, QrCode,
  ArrowRight, ShoppingBag, Zap, Package, Link2,
} from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Dinheiro', icon: Banknote, color: 'emerald' },
  { id: 'card', label: 'Cartão', icon: CreditCard, color: 'blue' },
  { id: 'pix', label: 'Pix', icon: QrCode, color: 'violet' },
];

/**
 * Scanner unificado: lê cartão (1-4 dig) OU etiqueta de balança (EAN-13 / prefixo 2).
 *
 * Fluxo:
 *   1. Cursor sempre focado no input — basta apontar leitor.
 *   2. Bipou cartão → carrega/cria comanda e mostra resumo + botão "Finalizar e Cobrar".
 *   3. Bipou etiqueta com comanda carregada → adiciona item automaticamente.
 *   4. Bipou etiqueta sem comanda → erro pedindo cartão.
 *   5. Após pagamento → reset automático, cursor volta pro input (pronto pro próximo).
 */
export default function UnifiedScanner({ operator, onSaleCompleted }) {
  const [code, setCode] = useState('');
  const [active, setActive] = useState(null); // comanda ativa
  const [cfg, setCfg] = useState(null);
  const [pricePerKg, setPricePerKg] = useState(0);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [linkDialog, setLinkDialog] = useState(null); // { code } quando não encontra produto
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [prefix, plu, mode, ppk] = await Promise.all([
          db.config.get('scaleBarcodePrefix'),
          db.config.get('scaleBarcodePluDigits'),
          db.config.get('scaleBarcodeMode'),
          db.config.get('buffetPricePerKg'),
        ]);
        setCfg({
          prefix: prefix?.value || '2',
          pluDigits: plu?.value || 5,
          mode: mode?.value || 'peso',
        });
        setPricePerKg(Number(ppk?.value) || 59.9);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Scanner] init config falhou — usando defaults:', err);
        setCfg({ prefix: '2', pluDigits: 5, mode: 'peso' });
        setPricePerKg(59.9);
      }
    })();
  }, []);

  const focusInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, []);

  // Auto-focus ao montar e sempre que `active` mudar
  useEffect(() => { focusInput(); }, [active, focusInput]);

  const handleScanCard = async (cardNumber) => {
    try {
      const { command, mode } = await getOrReuseCommand(cardNumber, operator?.id);
      setActive(command);
      const labels = {
        loaded: `Comanda #${command.number} carregada`,
        reused: `Cartão #${command.number} reutilizado — nova comanda aberta`,
        created: `Comanda #${command.number} criada`,
      };
      toast.success(labels[mode] || `Comanda #${command.number} pronta`);
      setCode('');
    } catch (err) {
      if (err?.code === 'NO_OPEN_SESSION') {
        toast.error('Caixa fechado', {
          description: 'Abra o caixa antes de abrir comandas novas.',
          duration: 6000,
        });
      } else {
        toast.error(err?.message || 'Falha ao abrir comanda');
      }
    } finally {
      setTimeout(focusInput, 60);
    }
  };

  const handleScanScale = async (result) => {
    // Precisa ter comanda ativa; se não, pede cartão
    if (!active) {
      toast.error('Bipe o cartão do cliente antes', {
        description: 'Sem comanda carregada o sistema não sabe onde lançar o item.',
      });
      setCode('');
      focusInput();
      return;
    }
    try {
      if (result.mode === 'peso') {
        const updated = await appendWeightToCommand(active.number, result.weightKg, pricePerKg, operator?.id);
        const subtotal = result.weightKg * pricePerKg;
        setActive(updated);
        toast.success(`+${result.weightKg.toFixed(3)} kg = ${formatCurrency(subtotal)}`, {
          description: `Comanda #${active.number} — Total ${formatCurrency(updated.total)}`,
        });
      } else {
        const item = {
          name: `Buffet (PLU ${result.plu})`,
          price: result.priceTotal,
          quantity: 1,
          beverageId: null,
        };
        await addItemToCommand(active.number, item, operator?.id);
        const updated = await db.commands.get(active.id);
        setActive(updated);
        toast.success(`+${formatCurrency(result.priceTotal)}`, {
          description: `Comanda #${active.number} — Total ${formatCurrency(updated?.total || 0)}`,
        });
      }
      setCode('');
    } catch (err) {
      toast.error(err?.message || 'Falha ao lançar item');
    } finally {
      setTimeout(focusInput, 60);
    }
  };

  const handleScanEan13Product = async (code) => {
    // Procura produto cadastrado
    const found = await findProductByBarcode(code);
    if (!found) {
      // Alerta + modal de cadastro rápido (precisa de comanda? não — é cadastro estrutural)
      setLinkDialog({ code });
      setCode('');
      toast.info('Código não cadastrado', {
        description: 'Associe este código a um produto do estoque.',
      });
      return;
    }
    // Encontrou — precisa de comanda ativa
    if (!active) {
      toast.error('Bipe o cartão do cliente antes', {
        description: `Produto "${found.product.name}" será adicionado depois do cartão.`,
      });
      setCode('');
      focusInput();
      return;
    }
    try {
      const item = {
        name: found.product.name,
        price: Number(found.product.price) || 0,
        quantity: 1,
        beverageId: found.kind === 'beverage' ? found.product.id : null,
      };
      await addItemToCommand(active.number, item, operator?.id);
      const updated = await db.commands.get(active.id);
      setActive(updated);
      toast.success(`+${found.product.name}`, {
        description: `${formatCurrency(item.price)} — Total ${formatCurrency(updated?.total || 0)}`,
      });
      setCode('');
    } catch (err) {
      toast.error(err?.message || 'Falha ao lançar produto');
    } finally {
      setTimeout(focusInput, 60);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!cfg || !code || busy) return;
    setBusy(true);
    try {
      let cls;
      try {
        cls = classifyInput(code, cfg);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Scanner] classifyInput throw:', err);
        toast.error('Código inválido', { description: String(err?.message || err) });
        setCode('');
        return;
      }
      if (cls.kind === 'card') {
        await handleScanCard(cls.cardNumber);
      } else if (cls.kind === 'scale') {
        await handleScanScale(cls.scale);
      } else if (cls.kind === 'ean13') {
        await handleScanEan13Product(cls.code);
      } else {
        toast.error(cls.reason || 'Código não reconhecido');
        setCode('');
      }
    } catch (err) {
      // Hotfix: blindagem geral. Qualquer erro inesperado vira toast em vez
      // de quebrar o React tree e tela branca.
      // eslint-disable-next-line no-console
      console.error('[Scanner] handleSubmit catch:', err);
      toast.error('Erro ao processar leitura', {
        description: String(err?.message || err).slice(0, 160),
        duration: 7000,
      });
      setCode('');
    } finally {
      setBusy(false);
      setTimeout(focusInput, 80);
    }
  };

  const resetAfterPayment = () => {
    setActive(null);
    setCode('');
    setPayOpen(false);
    setTimeout(focusInput, 80);
  };

  if (!cfg) return null;

  return (
    <>
      <div data-testid="unified-scanner" className={`relative overflow-hidden rounded-2xl mb-6 border-2 ${
        active ? 'bg-emerald-50/40 border-emerald-300' : 'bg-white border-slate-200'
      }`}>
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
              active ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
            }`}>
              <Scan size={22} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
                {active ? 'Comanda ativa' : 'Aguardando cartão'}
              </p>
              {active ? (
                <h3 className="font-heading text-lg font-black text-slate-900 flex items-baseline gap-2">
                  #{active.number}
                  <span className="font-mono text-sm font-bold text-emerald-700">
                    {formatCurrency(active.total || 0)}
                  </span>
                </h3>
              ) : (
                <h3 className="font-heading text-base font-bold text-slate-700">
                  Bipe o cartão do cliente (1-4 dígitos)
                </h3>
              )}
              <p className="text-[11px] text-slate-500 mt-0.5">
                {active
                  ? 'Bipe a etiqueta da balança para lançar itens, ou finalize abaixo.'
                  : `Modo ${cfg.mode === 'peso' ? 'Peso' : 'Preço'} • PLU ${cfg.pluDigits} dig • R$/kg ${pricePerKg.toFixed(2).replace('.', ',')}`}
              </p>
            </div>
            {active && (
              <button
                data-testid="unified-clear-btn"
                onClick={() => { setActive(null); focusInput(); }}
                title="Fechar comanda sem cobrar (volta pro aguardando)"
                className="shrink-0 w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 flex items-center justify-center"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Barcode size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                ref={inputRef}
                data-testid="unified-scanner-input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Bipe ou digite: cartão (ex: 5) ou etiqueta (13 dígitos)"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
                onFocus={(e) => e.target.select()}
                className="h-12 pl-9 text-base font-mono tracking-wider border-2 border-slate-200 rounded-xl"
                autoFocus
              />
            </div>
            <button
              data-testid="unified-scanner-submit"
              type="submit"
              disabled={busy || !code}
              className="h-12 px-6 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {busy ? '...' : <><Zap size={15} /> Processar</>}
            </button>
            {active && (
              <button
                data-testid="unified-checkout-btn"
                type="button"
                onClick={() => setPayOpen(true)}
                className="h-12 px-5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                Finalizar e Cobrar <ArrowRight size={16} />
              </button>
            )}
          </form>

          {/* Resumo curto da comanda ativa */}
          {active && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              <MiniStat label="Buffet" value={formatCurrency(active.buffetTotal || 0)} testid="unified-buffet" />
              <MiniStat label="Itens" value={formatCurrency(active.itemsTotal || 0)} testid="unified-items" />
              <MiniStat label="Total" value={formatCurrency(active.total || 0)} strong testid="unified-total" />
            </div>
          )}
        </div>
      </div>

      <PayDialog
        open={payOpen}
        onOpenChange={(o) => { if (!o) setPayOpen(false); }}
        command={active}
        operator={operator}
        onPaid={async () => {
          await onSaleCompleted?.();
          resetAfterPayment();
        }}
      />

      <LinkBarcodeDialog
        linkDialog={linkDialog}
        onClose={() => { setLinkDialog(null); focusInput(); }}
      />
    </>
  );
}

function MiniStat({ label, value, strong, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-emerald-100 rounded-xl px-3 py-2">
      <p className="text-[9px] uppercase tracking-wider font-bold text-emerald-600">{label}</p>
      <p className={`font-mono ${strong ? 'text-lg font-black text-slate-900' : 'text-sm font-bold text-slate-700'}`}>
        {value}
      </p>
    </div>
  );
}

/**
 * Dialog de checkout rápido acoplado ao scanner.
 * Para dinheiro pede valor recebido; outros métodos fecham direto.
 */
function PayDialog({ open, onOpenChange, command, operator, onPaid }) {
  const [method, setMethod] = useState('cash');
  const [received, setReceived] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setMethod('cash'); setReceived(''); }
  }, [open]);

  if (!command) return null;

  const total = Number(command.total || 0);
  const receivedNum = Number(String(received).replace(',', '.')) || 0;
  const change = Math.max(0, receivedNum - total);
  const canPay = method !== 'cash' || receivedNum >= total;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const details = { finalTotal: total };
      if (method === 'cash') {
        details.amountReceived = receivedNum;
        details.changeGiven = change;
      }
      await closeCommand(command.id, method, details, operator);
      toast.success(`Comanda #${command.number} paga em ${method}`, {
        description: method === 'cash' && change > 0 ? `Troco: ${formatCurrency(change)}` : undefined,
      });
      await onPaid?.();
    } catch (err) {
      toast.error(err?.message || 'Falha ao fechar comanda');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent data-testid="unified-pay-dialog" className="max-w-md bg-white border-2 border-emerald-200 rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag size={20} className="text-emerald-600" />
            Finalizar Comanda #{command.number}
          </DialogTitle>
          <DialogDescription>
            Escolha a forma de pagamento. Total: <strong className="font-mono text-slate-900">{formatCurrency(total)}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          {PAYMENT_METHODS.map((m) => {
            const Icon = m.icon;
            const selected = method === m.id;
            return (
              <button
                key={m.id}
                data-testid={`unified-pay-method-${m.id}`}
                onClick={() => setMethod(m.id)}
                className={`py-4 rounded-xl border-2 text-xs font-bold transition-all flex flex-col items-center gap-1.5 ${
                  selected
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 scale-[1.02] shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
                }`}
              >
                <Icon size={20} />
                {m.label}
              </button>
            );
          })}
        </div>

        {method === 'cash' && (
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
              Valor recebido (R$)
            </label>
            <Input
              data-testid="unified-pay-received"
              type="text"
              inputMode="decimal"
              value={received}
              onChange={(e) => setReceived(e.target.value.replace(/[^\d.,]/g, ''))}
              placeholder={total.toFixed(2).replace('.', ',')}
              className="h-14 text-2xl font-mono font-bold text-center border-2 border-emerald-300 rounded-xl"
              autoFocus
            />
            {receivedNum > 0 && (
              <div className={`mt-2 text-xs text-center font-bold ${change > 0 ? 'text-emerald-700' : receivedNum < total ? 'text-rose-600' : 'text-slate-500'}`}>
                {change > 0 ? `Troco: ${formatCurrency(change)}` : receivedNum < total ? `Falta: ${formatCurrency(total - receivedNum)}` : 'Valor exato'}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <button
            data-testid="unified-pay-cancel"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            data-testid="unified-pay-confirm"
            onClick={handleConfirm}
            disabled={busy || !canPay}
            className="flex-1 h-11 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? '...' : <><CheckCircle2 size={15} /> Cobrar</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Dialog de cadastro rápido: associa um código de barras EAN-13 não-reconhecido
 * a uma bebida existente. Operador escolhe da lista e confirma.
 */
function LinkBarcodeDialog({ linkDialog, onClose }) {
  const [beverages, setBeverages] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (linkDialog) {
      (async () => {
        const bev = await db.beverages.orderBy('name').toArray();
        setBeverages(bev);
        setSelectedId('');
        setSearch('');
      })();
    }
  }, [linkDialog]);

  if (!linkDialog) return null;

  const filtered = search
    ? beverages.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()))
    : beverages;

  const handleConfirm = async () => {
    if (!selectedId) {
      toast.error('Selecione um produto');
      return;
    }
    setBusy(true);
    try {
      const bev = await linkBarcodeToBeverage(Number(selectedId), linkDialog.code);
      toast.success(`Código associado a "${bev.name}"`, {
        description: 'Agora bipar esse código adiciona o produto direto na comanda.',
      });
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Falha ao associar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!linkDialog} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="unified-link-dialog" className="max-w-md bg-white border-2 border-amber-200 rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <Link2 size={20} className="text-amber-600" />
            Associar código de barras
          </DialogTitle>
          <DialogDescription>
            Código lido: <strong className="font-mono text-slate-900">{linkDialog.code}</strong>.
            Escolha a bebida correspondente no estoque.
          </DialogDescription>
        </DialogHeader>

        <Input
          data-testid="unified-link-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar bebida..."
          className="h-10 border-2 border-slate-200 rounded-lg"
        />

        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              <Package size={28} className="mx-auto mb-1 text-slate-300" />
              {beverages.length === 0 ? 'Nenhuma bebida cadastrada' : 'Nenhum resultado'}
            </div>
          ) : (
            filtered.map((b) => {
              const sel = Number(selectedId) === b.id;
              return (
                <button
                  key={b.id}
                  data-testid={`unified-link-bev-${b.id}`}
                  onClick={() => setSelectedId(String(b.id))}
                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors ${
                    sel ? 'bg-emerald-50' : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className="min-w-0">
                    <p className={`text-sm font-bold truncate ${sel ? 'text-emerald-800' : 'text-slate-800'}`}>
                      {b.name}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {b.category || '—'} • Estoque: {b.stock || 0}
                      {b.barcode && <span className="text-amber-600"> • Já tem código ({b.barcode})</span>}
                    </p>
                  </div>
                  <span className="font-mono text-sm font-bold text-slate-700 shrink-0">
                    {formatCurrency(b.price || 0)}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter className="gap-2">
          <button
            data-testid="unified-link-cancel"
            onClick={onClose}
            disabled={busy}
            className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Agora não
          </button>
          <button
            data-testid="unified-link-confirm"
            onClick={handleConfirm}
            disabled={busy || !selectedId}
            className="flex-1 h-11 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 disabled:opacity-50"
          >
            {busy ? '...' : 'Associar'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

